import React from 'react'
import {Helmet} from "react-helmet"
import {GoogleOAuthProvider} from '@react-oauth/google'
import {QueryClient, QueryClientProvider} from 'react-query'
import {IntlProvider} from "react-intl"
import {LOCALES} from "./i18n/locales"
import {useSolv} from "./components/SolvProvider"
import {PublicClientApplication} from "@azure/msal-browser";
import {MsalProvider} from "@azure/msal-react";
import Routes from "./Routes";
import {determineLocale, useMessages} from "./components/IntlUtils";
import {determineTheme} from "./components/Utils";
import getLogger from "./components/Logging"
import "./App.css"

const log = getLogger("App")

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchInterval: -1,
      refetchIntervalInBackground: -1,
    }
  }
})

function App() {

  const {status, env, session, supportMailLink} = useSolv()

  const locale = determineLocale(session?.user?.lang)
  const theme = determineTheme(session?.user?.theme)

  const messages = useMessages({locale})

  const msalInstance = new PublicClientApplication({
    auth: {
      clientId: env.microsoftClientId,
      authority: "https://login.microsoftonline.com/common",
      redirectUri: env.consoleUrl,
    },
    cache: {
      cacheLocation: "sessionStorage",
      storeAuthStateInCookie: false,
    }
  });

  if (status !== "READY" || !messages) {
    return ("...")
  }

  return (
      <GoogleOAuthProvider clientId={env.googleClientId}>
        <MsalProvider instance={msalInstance}>
          <QueryClientProvider client={queryClient}>
            <IntlProvider
              key={locale}
              messages={messages}
              locale={locale}
              defaultLocale={LOCALES.en}
              defaultRichTextElements={{
                a_www: chunks => <a className="text-link" href={env.wwwUrl}>{chunks}</a>,
                a_contact: chunks => <a className="text-link" href={env.contactUrl} target="_blank">{chunks}</a>,
                a_contact_mail: chunks => {
                  switch (env.brandId) {
                    case "ELCT": return <a className="text-link" href="mailto:support@elct.io">{chunks?.length > 0 ? chunks : "support@elct.io"}</a>
                    case "SOLV": return <a className="text-link" href="mailto:support@solv.technology">{chunks?.length > 0 ? chunks :  "support@solv.technology"}</a>
                    case "PETSOLV": return <a className="text-link" href="mailto:support+petsolv@solv.technology">{chunks?.length > 0 ? chunks : "support+petsolv@solv.technology"}</a>
                  }
                },
                a_support_mail: chunks => supportMailLink(chunks),
                a_tos: chunks => <a className="text-link" href={env.termsUrl} target="_blank">{chunks}</a>,
                a_privacy: chunks => <a className="text-link" href={env.privacyUrl} target="_blank">{chunks}</a>,
                cp_icon: () => <>&copy;</>,
                a_recaptcha: chunks => <a className="text-link" href="https://en.wikipedia.org/wiki/ReCAPTCHA" target="_blank">{chunks}</a>,
                a_signin: chunks => <a className="text-link" href="/signin">{chunks}</a>,
                a_signup: chunks => <a className="text-link" href="/signup">{chunks}</a>,
                a_help: chunks => <a className="text-link" href="https://help.solv.technology" target="_blank">{chunks}</a>,
                a_help_access_level: chunks => <a className="icon-link" title={chunks} href="https://help.solv.technology/article/access-levels-and-permissions" target="_blank">
                  <span className="fa-layers">
                    <i className="far fa-circle" style={{color: "var(--form-control-border-color)"}}
                       data-fa-transform="grow-4"></i>
                    <i className="fas fa-question" data-fa-transform="shrink-6"></i>
                  </span>
                </a>,
                a_help_broadcast_name: chunks => <a className="icon-link" title={chunks} href="https://help.solv.technology/article/broadcast_name" target="_blank">
                  <span className="fa-layers">
                    <i className="far fa-circle" style={{color: "var(--form-control-border-color)"}}  data-fa-transform="grow-4"></i>
                    <i className="fas fa-question" data-fa-transform="shrink-6"></i>
                  </span>
                </a>,
                a_help_broadcast_image: chunks => <a className="icon-link" title={chunks} href="https://help.solv.technology/article/broadcast_image" target="_blank">
                  <span className="fa-layers">
                    <i className="far fa-circle" style={{color: "var(--form-control-border-color)"}}  data-fa-transform="grow-4"></i>
                    <i className="fas fa-question" data-fa-transform="shrink-6"></i>
                  </span>
                </a>,
                a_help_broadcast_locations: chunks => <a className="icon-link" title={chunks} href="https://help.solv.technology/article/broadcast_locations" target="_blank">
                  <span className="fa-layers">
                    <i className="far fa-circle" style={{color: "var(--form-control-border-color)"}}  data-fa-transform="grow-4"></i>
                    <i className="fas fa-question" data-fa-transform="shrink-6"></i>
                  </span>
                </a>,
                a_help_broadcast_budget: chunks => <a className="icon-link" title={chunks} href="https://help.solv.technology/article/broadcast_budget" target="_blank">
                  <span className="fa-layers">
                    <i className="far fa-circle" style={{color: "var(--form-control-border-color)"}}  data-fa-transform="grow-4"></i>
                    <i className="fas fa-question" data-fa-transform="shrink-6"></i>
                  </span>
                </a>,
                a_help_broadcast_duration: chunks => <a className="icon-link" title={chunks} href="https://help.solv.technology/article/broadcast_duration" target="_blank">
                  <span className="fa-layers">
                    <i className="far fa-circle" style={{color: "var(--form-control-border-color)"}}  data-fa-transform="grow-4"></i>
                    <i className="fas fa-question" data-fa-transform="shrink-6"></i>
                  </span>
                </a>,
                a_help_image_format: chunks => <a className="icon-link" title={chunks} href="https://help.solv.technology/article/image-format-specs-and-requirements" target="_blank">
                  <span className="fa-layers">
                    <i className="far fa-circle" style={{color: "var(--form-control-border-color)"}}  data-fa-transform="grow-4"></i>
                    <i className="fas fa-question" data-fa-transform="shrink-6"></i>
                  </span>
                </a>,
                a_help_estimations: chunks => <a className="icon-link" title={chunks} href="https://help.solv.technology/article/populations-views-and-reach" target="_blank">
                  <span className="fa-layers">
                    <i className="far fa-circle" style={{color: "var(--form-control-border-color)"}}  data-fa-transform="grow-4"></i>
                    <i className="fas fa-question" data-fa-transform="shrink-6"></i>
                  </span>
                </a>,
                a_help_markdown: chunks => <a className="text-link" href="https://help.solv.technology/article/formatting-text-with-markdown" target="_blank">{chunks}</a>,
                a_wikipedia_iso_3166_1_alpha_2: chunks => <a className="icon-link" title={chunks} href="https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2" target="_blank"><i className="far fa-question-circle"></i></a>, a_wikipedia_iso_4217: chunks => <a className="text-link" href="https://en.wikipedia.org/wiki/ISO_4217" target="_blank">{chunks}</a>,
                acc: chunks => <span style={{color: "var(--app-accent-color)"}}>{chunks}</span>,
                br: chunks => <br/>,
                b: chunks => <strong>{chunks}</strong>,
                but: chunks => <span className="but">{chunks}</span>,
                em: chunks => <em>{chunks}</em>,
                emd: chunks => <em className="text-danger">{chunks}</em>,
                emw: chunks => <em className="text-warning">{chunks}</em>,
                i: chunks => <i>{chunks}</i>,
                p: chunks => <p>{chunks}</p>,
                h1: chunks => <h1>{chunks}</h1>,
                h2: chunks => <h2>{chunks}</h2>,
                h3: chunks => <h3>{chunks}</h3>,
                h4: chunks => <h4>{chunks}</h4>,
                h5: chunks => <h5>{chunks}</h5>,
                mark: chunks => <mark>{chunks}</mark>,
                code: chunks => <code>{chunks}</code>,
                kbd: chunks => <kbd>{chunks}</kbd>,
                sup: chunks => <sup>{chunks}</sup>,
                small: chunks => <small>{chunks}</small>,
                ul: chunks => <ul style={{display: "flex", flexDirection: "column", listStyle: "disc", gap: "0.5em", marginTop: "0.5em", marginBottom: "0.5em", marginLeft: "1em", paddingLeft: "0"}}>{chunks}</ul>,
                ol: chunks => <ol style={{display: "flex", flexDirection: "column", gap: "0.5em", marginTop: "0.5em", marginBottom: "0.5em", marginLeft: "1em", paddingLeft: "0"}}>{chunks}</ol>,
                li: chunks => <li>{chunks}</li>,
                i_info_circle: chucks => <i className="fas fa-info-circle"></i>,
                sp: chucks => <span className="ml-1"></span>,
                span: chunks => <span>{chunks}</span>,
                muted: chunks => <span className="text-muted">{chunks}</span>,
                warn: chucks => <span className="text-warning">{chucks}</span>,
                error: chucks => <span className="text-danger">{chucks}</span>
              }}>
              {
                env ? (
                  <Helmet>
                    <title>{env.displayName}</title>
                    <meta name="description" content={env.displayName}/>
                    <style type="text/css">{`
                      :root {
                        --app-accent-color: ${env.accentColor};
                        --app-panel-color: ${env.panelColor};                        
                        --app-background: url(${env.backgroundImageUrl}) center center / cover no-repeat;
                      }
                      `}
                    </style>
                    {
                      theme &&
                        <link rel="stylesheet" href={`https://sas.solv.technology/a/themes/${theme.toLowerCase()}.css`}/>
                    }
                    <link rel="icon" href={env.iconUrl}/>
                    <link rel="apple-touch-icon" href={env.iconUrl}/>
                  </Helmet>
                ) : (
                  <></>
                )
              }
              <Routes/>
            </IntlProvider>
          </QueryClientProvider>
        </MsalProvider>
      </GoogleOAuthProvider>
  )
}

export default App;
